import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { validateDemoCredentials, isDemoMode, getDemoCredentials } from '../config/app-config';
import { APP_CONFIG } from '../config/app-config';
import {
  getToken as getSessionToken,
  initializeTokenStorage,
  removeToken as removeSessionToken,
  setToken as setSessionToken
} from '../utils/authToken';
import { readStoredMetaAttribution } from '../utils/metaClickIds';

// Updated KYC status type with complete flow
type KYCStatus = 'not_started' | 'documents_uploaded' | 'under_review' | 'verified' | 'rejected' | 'ussd_basic';

interface User {
  id: string;
  name: string;
  identifier: string; // SA mobile number for login
  identifierType: 'phone' | 'account' | 'username';
  phoneNumber: string; // Always set for login users (SA mobile number)
  walletId: string;
  kycStatus: KYCStatus;
  kycVerified: boolean; // Computed property for easy access
  kycTier: number | null; // 0=USSD basic, 1=web ID verified, 2=web ID+POA verified
  createdAt?: string; // User registration date
  idNumberMasked?: string | null;
  idType?: string | null;
  idTypeLabel?: string | null;
}

interface PendingAppPassword {
  token: string; // scoped setAppPasswordToken (10-min, purpose=set_app_password)
  phoneNumber: string;
  firstName?: string | null;
  lastName?: string | null;
}

interface PendingLoginOtp {
  token: string; // scoped verifyOtpToken (10-min, purpose=verify_login_otp)
  phoneNumber: string;
  firstName?: string | null;
  lastName?: string | null;
}

interface AuthContextType {
  user: User | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register?: (registrationData: RegistrationData) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  refreshToken: () => Promise<void>;
  refreshUserStatus: () => Promise<void>;
  requiresKYC: (action?: string) => boolean;
  changePassword: (currentPassword: string, newPassword: string, confirmNewPassword: string) => Promise<void>;
  // USSD-PIN → app-password graduation (Option A+B, 2026-08-14).
  // pendingLoginOtp is set by login() when the backend returns OTP_REQUIRED
  // (a non-graduated USSD user logged in with their PIN and was sent an SMS
  // OTP). verifyLoginOtp() verifies the OTP; on success the backend returns
  // APP_PASSWORD_REQUIRED + setAppPasswordToken, which is stashed into
  // pendingAppPassword. setAppPassword() consumes it to graduate.
  pendingLoginOtp: PendingLoginOtp | null;
  verifyLoginOtp: (otp: string) => Promise<void>;
  clearPendingLoginOtp: () => void;
  pendingAppPassword: PendingAppPassword | null;
  setAppPassword: (newPassword: string, confirmNewPassword: string) => Promise<void>;
  clearPendingAppPassword: () => void;
}

interface LoginCredentials {
  identifier: string; // SA mobile number only for login
  password: string;
}

interface RegistrationData {
  name?: string;
  firstName: string;
  surname: string;
  identifier: string;
  confirmPhoneNumber: string;
  idNumber: string;
  idType: 'south_african_id' | 'passport';
  email: string;
  password: string;
  identifierType: 'phone' | 'account' | 'username';
  referralCode?: string;
  partnerSlug?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Central mapping: normalize backend user payload into our frontend User shape
function mapBackendUserToContextUser(raw: any): User {
  // Prefer explicit phoneNumber; fall back to legacy keys
  const phone = normalizeSAMobileNumber(
    raw?.phoneNumber || raw?.phone || raw?.identifier || ''
  );
  return {
    id: String(raw?.id ?? ''),
    name: raw?.name || `${raw?.firstName || ''} ${raw?.lastName || ''}`.trim(),
    identifier: phone,
    identifierType: 'phone',
    phoneNumber: phone,
    walletId: String(raw?.walletId || raw?.wallet?.walletId || ''),
    kycStatus: (raw?.kycStatus as KYCStatus) || 'not_started',
    kycVerified: raw?.kycStatus === 'verified',
    kycTier: raw?.kyc_tier != null ? Number(raw.kyc_tier) : null,
    createdAt: raw?.createdAt,
    idNumberMasked: raw?.idNumberMasked ?? null,
    idType: raw?.idType ?? null,
    idTypeLabel: raw?.idTypeLabel ?? null,
  };
}

// SA Mobile Number validation for login
const validateSAMobileNumber = (phoneNumber: string): boolean => {
  const saPhonePattern = /^(\+27|27|0)[6-8][0-9]{8}$/;
  const cleanNumber = phoneNumber.replace(/\s/g, '');
  return saPhonePattern.test(cleanNumber);
};

// Input type detection utility for registration (multi-input support)
const detectInputType = (input: string): 'phone' | 'account' | 'username' | 'unknown' => {
  const cleanInput = input.trim();
  
  // Phone number patterns (SA format)
  const phonePattern = /^(\+27|27|0)?[6-8][0-9]{8}$/;
  if (phonePattern.test(cleanInput.replace(/\s/g, ''))) {
    return 'phone';
  }
  
  // Account number pattern (8-12 digits only)
  const accountPattern = /^[0-9]{8,12}$/;
  if (accountPattern.test(cleanInput)) {
    return 'account';
  }
  
  // Username pattern (4-32 chars, letters/numbers/periods/underscores)
  const usernamePattern = /^[a-zA-Z0-9._]{4,32}$/;
  if (usernamePattern.test(cleanInput)) {
    return 'username';
  }
  
  return 'unknown';
};

// Normalize SA mobile number to E.164 format (+27XXXXXXXXX)
const normalizeSAMobileNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  const digits = phoneNumber.replace(/\D/g, '');
  
  // 0XXXXXXXXX -> +27XXXXXXXXX
  if (digits.startsWith('0') && digits.length === 10) {
    return `+27${digits.slice(1)}`;
  }
  
  // 27XXXXXXXXX -> +27XXXXXXXXX
  if (digits.startsWith('27') && digits.length === 11) {
    return `+${digits}`;
  }
  
  // Already +27XXXXXXXXX
  if (phoneNumber.startsWith('+27') && digits.length === 11) {
    return phoneNumber;
  }
  
  // Fallback: try to make it valid
  return digits.startsWith('27') ? `+${digits}` : `+27${digits}`;
};

// Helper function to safely parse JSON responses
const safeJsonParse = async (response: Response): Promise<any> => {
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      console.error('Failed to parse JSON response:', error);
      return null;
    }
  } else {
    // Response is not JSON (likely HTML error page)
    const text = await response.text();
    console.error('Received non-JSON response:', text);
    return null;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // USSD-PIN → app-password graduation payload (Option A+B, 2026-08-14).
  const [pendingAppPassword, setPendingAppPassword] = useState<PendingAppPassword | null>(null);
  const [pendingLoginOtp, setPendingLoginOtp] = useState<PendingLoginOtp | null>(null);

  useEffect(() => {
    // Check for stored auth token on app start
    checkAuthStatus();
    
    // REMOVED: Harmful polling interval for token refresh
    // Token refresh will now happen on-demand when API calls fail
    // This follows Mojaloop and banking best practices for scalability
    // 
    // FUTURE: Will implement proper token refresh on API failure
    // FUTURE: Will add WebSocket-based token validation
  }, []);

  const checkAuthStatus = async () => {
    try {
      await initializeTokenStorage();
      const token = getSessionToken();
      
      // If no token found, clear user state (user needs to log in)
      if (!token) {
        setUser(null);
        await removeSessionToken();
        setIsLoading(false);
        return;
      }
      
      if (token.startsWith('demo-token-')) {
        // Demo mode - restore user session
        const demoCredentials = getDemoCredentials();
        
        // Check for stored KYC status in demo mode
        const storedKYCStatus = localStorage.getItem('mymoolah_kyc_status') as KYCStatus;
        const kycStatus = storedKYCStatus || 'not_started';
        
        const mockUser: User = {
          id: 'demo-user-001',
          name: 'Demo User',
          identifier: normalizeSAMobileNumber(demoCredentials.phoneNumber),
          identifierType: 'phone',
          phoneNumber: normalizeSAMobileNumber(demoCredentials.phoneNumber),
          walletId: 'wallet-demo-001',
          kycStatus,
          kycVerified: kycStatus === 'verified',
          kycTier: kycStatus === 'verified' ? 1 : null
        };
        setUser(mockUser);
      } else {
        // Real authentication - validate token with backend
        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const responseData = await safeJsonParse(response);
          if (responseData && responseData.user) {
            setUser(mapBackendUserToContextUser(responseData.user));
          } else {
            setUser(null);
            await removeSessionToken();
          }
        } else {
          // Token is invalid or expired - clear user state
          setUser(null);
          await removeSessionToken();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      await removeSessionToken();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      // Validate SA mobile number format
      if (!validateSAMobileNumber(credentials.identifier)) {
        throw new Error('Please enter a valid South African mobile number');
      }

      // Simulate API call delay for realistic UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (isDemoMode()) {
        // Demo authentication - check against demo credentials
        const normalizedPhoneNumber = normalizeSAMobileNumber(credentials.identifier);
        const isValidCredentials = validateDemoCredentials(normalizedPhoneNumber, credentials.password);
        
        if (isValidCredentials) {
          // Check for stored KYC status in demo mode
          const storedKYCStatus = localStorage.getItem('mymoolah_kyc_status') as KYCStatus;
          const kycStatus = storedKYCStatus || 'not_started';
          
          // Create mock user for demo
          const mockUser: User = {
            id: 'demo-user-001',
            name: 'Demo User',
            identifier: normalizedPhoneNumber,
            identifierType: 'phone',
            phoneNumber: normalizedPhoneNumber,
            walletId: 'wallet-demo-001',
            kycStatus,
            kycVerified: kycStatus === 'verified',
            kycTier: kycStatus === 'verified' ? 1 : null
          };
          
          const mockToken = 'demo-token-' + Date.now();
          await setSessionToken(mockToken);
          setUser(mockUser);
        } else {
          throw new Error('Invalid mobile number or password. Please check your credentials.');
        }
      } else {
        // Production authentication with backend
        const normalizedPhoneNumber = normalizeSAMobileNumber(credentials.identifier);
        
        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identifier: normalizedPhoneNumber,
            password: credentials.password,
          }),
        });

        if (response.ok) {
          const responseData = await safeJsonParse(response);
          if (responseData && responseData.user && responseData.token) {
            await setSessionToken(responseData.token);
            setUser(mapBackendUserToContextUser(responseData.user));
          } else {
            throw new Error('Invalid response from server. Please try again.');
          }
        } else {
          const errorData = await safeJsonParse(response);

          // USSD-PIN → app-password graduation (Option A+B, 2026-08-14): a
          // non-graduated USSD user logged in with their PIN. The backend now
          // requires an SMS OTP step-up before graduation — it returns 403 +
          // errorCode OTP_REQUIRED and a scoped verifyOtpToken. Stash the
          // payload and throw a tagged error so the LoginPage routes to the
          // VerifyLoginOtpPage. (After OTP verification the backend returns
          // APP_PASSWORD_REQUIRED + setAppPasswordToken, handled below.)
          if (errorData?.errorCode === 'OTP_REQUIRED' && errorData?.verifyOtpToken) {
            setPendingLoginOtp({
              token: errorData.verifyOtpToken,
              phoneNumber: errorData?.user?.phone || credentials.identifier,
              firstName: errorData?.user?.firstName ?? null,
              lastName: errorData?.user?.lastName ?? null,
            });
            const otpErr = new Error(errorData?.message || 'A login code was sent to your phone. Enter it to continue.');
            (otpErr as any).code = 'OTP_REQUIRED';
            throw otpErr;
          }

          // After the OTP step-up is verified, the backend returns
          // APP_PASSWORD_REQUIRED + setAppPasswordToken. Stash it and throw a
          // tagged error so the VerifyLoginOtpPage routes to SetAppPasswordPage.
          if (errorData?.errorCode === 'APP_PASSWORD_REQUIRED' && errorData?.setAppPasswordToken) {
            setPendingAppPassword({
              token: errorData.setAppPasswordToken,
              phoneNumber: errorData?.user?.phone || credentials.identifier,
              firstName: errorData?.user?.firstName ?? null,
              lastName: errorData?.user?.lastName ?? null,
            });
            const gradErr = new Error(errorData?.message || 'Please set an app password to finish logging in.');
            (gradErr as any).code = 'APP_PASSWORD_REQUIRED';
            throw gradErr;
          }

          throw new Error(errorData?.message || `Login failed (${response.status}). Please try again.`);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    void removeSessionToken();
    localStorage.removeItem('mymoolah_kyc_status');
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      const token = getSessionToken();
      if (!token) return;

      if (token.startsWith('demo-token-')) {
        // Demo mode - just refresh the timestamp
        const newToken = 'demo-token-' + Date.now();
        await setSessionToken(newToken);
        return;
      }

      // Real authentication - refresh token with backend
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const responseData = await safeJsonParse(response);
        if (responseData && responseData.token) {
          await setSessionToken(responseData.token);
        } else {
          logout();
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  // Internal helper: update KYC status in demo mode (localStorage + user state).
  // In production, KYC status is owned by the backend — the wallet frontend
  // refreshes it via refreshUserStatus() which reads /api/v1/users/me. The old
  // production path (POST /api/v1/kyc/update-status) was removed because that
  // endpoint requires portal admin auth, not wallet user auth.
  const updateKYCStatus = async (status: KYCStatus) => {
    if (!isDemoMode()) return;
    localStorage.setItem('mymoolah_kyc_status', status);
    if (user) {
      setUser({
        ...user,
        kycStatus: status,
        kycVerified: status === 'verified',
        kycTier: status === 'verified' ? Math.max(user.kycTier ?? 0, 1) : user.kycTier
      });
    }
  };

  const refreshUserStatus = async () => {
    try {
      if (isDemoMode()) {
        // Demo mode - simulate status check
        const storedStatus = localStorage.getItem('mymoolah_kyc_status') as KYCStatus;
        
        // Simulate status progression for demo
        if (storedStatus === 'documents_uploaded') {
          // Randomly progress to under_review after some time
          const uploadTime = localStorage.getItem('mymoolah_upload_time');
          const currentTime = Date.now();
          
          if (!uploadTime) {
            localStorage.setItem('mymoolah_upload_time', currentTime.toString());
          } else {
            const timeDiff = currentTime - parseInt(uploadTime);
            // Progress to under_review after 30 seconds for demo
            if (timeDiff > 30000 && Math.random() > 0.3) {
              await updateKYCStatus('under_review');
            }
          }
        } else if (storedStatus === 'under_review') {
          // Randomly progress to verified after some time
          const reviewTime = localStorage.getItem('mymoolah_review_time');
          const currentTime = Date.now();
          
          if (!reviewTime) {
            localStorage.setItem('mymoolah_review_time', currentTime.toString());
          } else {
            const timeDiff = currentTime - parseInt(reviewTime);
            // Progress to verified after 60 seconds for demo
            if (timeDiff > 60000 && Math.random() > 0.5) {
              await updateKYCStatus('verified');
              localStorage.removeItem('mymoolah_upload_time');
              localStorage.removeItem('mymoolah_review_time');
            }
          }
        }
      } else {
        // Production mode - fetch latest status from backend
        const token = getSessionToken();
        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const responseData = await safeJsonParse(response);
          if (responseData) {
            // Backend returns { success: true, data: { ... } } or user fields at top level
            const userData = responseData.data || responseData;
            setUser(mapBackendUserToContextUser(userData));
          }
        }
      }
    } catch (error) {
      console.error('User status refresh failed:', error);
    }
  };

  // Change password (demo aware)
  const changePassword = async (currentPassword: string, newPassword: string, confirmNewPassword: string) => {
    if (newPassword !== confirmNewPassword) {
      throw new Error('New password and confirmation do not match');
    }
    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters');
    }

    if (isDemoMode()) {
      // Simulate success in demo mode
      return;
    }

    const token = getSessionToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword })
    });

    if (!response.ok) {
      const data = await safeJsonParse(response);
      throw new Error(data?.message || 'Failed to change password');
    }
  };

  // Check if KYC is required for specific actions
  const requiresKYC = (action?: string): boolean => {
    if (!user) return true;
    
    const kycStatus = user.kycStatus;
    
    // Allow browsing and deposits without KYC
    if (action === 'browse' || action === 'deposit') {
      return false;
    }
    
    // Require KYC for transactions
    if (action === 'send' || action === 'transfer' || action === 'withdraw') {
      return kycStatus !== 'verified';
    }
    
    // Default: require KYC if not verified
    return kycStatus !== 'verified';
  };

  // FIXED: Registration function with proper backend field mapping
  const register = async (registrationData: RegistrationData) => {
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (isDemoMode()) {
        // Demo registration
        const mockUser: User = {
          id: 'demo-user-' + Date.now(),
          name: `${registrationData.firstName} ${registrationData.surname}`.trim(),
          identifier: registrationData.identifierType === 'phone' ? 
            normalizeSAMobileNumber(registrationData.identifier) : 
            registrationData.identifier, // Normalize phone numbers for consistency
          identifierType: registrationData.identifierType,
          phoneNumber: registrationData.identifierType === 'phone' ? 
            normalizeSAMobileNumber(registrationData.identifier) : 
            '', // Empty for non-phone registrations, will be set during profile update
          walletId: 'wallet-demo-' + Date.now(),
          kycStatus: 'not_started',
          kycVerified: false,
          kycTier: null,
          createdAt: new Date().toISOString()
        };
        
        const mockToken = 'demo-token-' + Date.now();
        await setSessionToken(mockToken);
        setUser(mockUser);
      } else {
        // FIXED: Production registration with correct backend field mapping
        const metaAttribution = readStoredMetaAttribution();
        let backendPayload: any;
        
        if (registrationData.identifierType === 'phone') {
          // For phone numbers, send as phoneNumber field (what backend expects)
          const cleanPhoneNumber = normalizeSAMobileNumber(registrationData.identifier);
          const cleanConfirmPhone = normalizeSAMobileNumber(registrationData.confirmPhoneNumber);
          backendPayload = {
            firstName: registrationData.firstName,
            surname: registrationData.surname,
            name: registrationData.name || `${registrationData.firstName} ${registrationData.surname}`.trim(),
            email: registrationData.email,
            phoneNumber: cleanPhoneNumber,
            confirmPhoneNumber: cleanConfirmPhone,
            password: registrationData.password,
            idType: registrationData.idType,
            idNumber: registrationData.idNumber,
            ...(registrationData.referralCode && { referralCode: registrationData.referralCode }),
            ...(registrationData.partnerSlug && { partnerSlug: registrationData.partnerSlug }),
            ...metaAttribution,
          };
        } else {
          // For account numbers and usernames, send as identifier
          // (Note: Backend may need to be updated to handle these cases)
          backendPayload = {
            firstName: registrationData.firstName,
            surname: registrationData.surname,
            name: registrationData.name || `${registrationData.firstName} ${registrationData.surname}`.trim(),
            email: registrationData.email,
            identifier: registrationData.identifier,
            identifierType: registrationData.identifierType,
            confirmPhoneNumber: registrationData.confirmPhoneNumber,
            password: registrationData.password,
            idType: registrationData.idType,
            idNumber: registrationData.idNumber,
            ...(registrationData.referralCode && { referralCode: registrationData.referralCode }),
            ...(registrationData.partnerSlug && { partnerSlug: registrationData.partnerSlug }),
            ...metaAttribution,
          };
        }

        try {
          const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/register`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(backendPayload)
          });

          if (response.ok) {
            const responseData = await safeJsonParse(response);
            if (responseData && responseData.user && responseData.token) {
              await setSessionToken(responseData.token);
              setUser(mapBackendUserToContextUser(responseData.user));
            } else {
              throw new Error('Invalid response from server. Please try again.');
            }
          } else {
            // Handle different error response types
            const errorData = await safeJsonParse(response);
            
            let errorMessage = 'Registration failed. Please try again.';
            
            if (response.status === 404) {
              errorMessage = 'Registration service is currently unavailable. Please check that your backend is running on http://localhost:3001';
            } else if (response.status === 409) {
              errorMessage = 'An account with this information already exists.';
            } else if (response.status === 400) {
              // Handle validation errors
              if (errorData) {
                // Prefer field-specific errors when present
                if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                  errorMessage = errorData.errors
                    .map((e: any) => `${e.field}: ${e.message}`)
                    .join(' • ');
                } else if (errorData.message) {
                  const msg = String(errorData.message).toLowerCase();
                  if (msg.includes('phone')) {
                    errorMessage = 'Invalid phone number format. Please use SA mobile number (0XX XXX XXXX).';
                  } else if (msg.includes('email')) {
                    errorMessage = 'Invalid email address format.';
                  } else if (msg.includes('password')) {
                    errorMessage = 'Password does not meet security requirements.';
                  } else if (msg.includes('id')) {
                    errorMessage = errorData.message;
                  } else {
                    errorMessage = errorData.message;
                  }
                }
              } else {
                errorMessage = 'Invalid registration data. Please check all fields.';
              }
            } else if (response.status >= 500) {
              errorMessage = 'Server error. Please try again later.';
            } else if (errorData && errorData.message) {
              errorMessage = errorData.message;
            }
            
            console.error('❌ Registration failed:', {
              status: response.status,
              statusText: response.statusText,
              error: errorData
            });
            
            throw new Error(errorMessage);
          }
        } catch (networkError) {
          if (networkError instanceof TypeError && networkError.message.includes('fetch')) {
            // Network connectivity issue
            throw new Error('Cannot connect to server. Please check that your backend is running on http://localhost:3001');
          } else {
            throw networkError;
          }
        }
      }
    } catch (error) {
      console.error('🔥 Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // USSD-PIN → app-password graduation — OTP step-up (Option A+B, 2026-08-14).
  // Consumes the scoped verifyOtpToken stashed by login() when a
  // non-graduated USSD user logged in with their PIN. On OTP success the
  // backend returns APP_PASSWORD_REQUIRED + setAppPasswordToken, which we
  // stash into pendingAppPassword so the VerifyLoginOtpPage can route to
  // SetAppPasswordPage.
  const verifyLoginOtp = async (otp: string) => {
    if (!pendingLoginOtp?.token) {
      throw new Error('No active login verification session. Please log in again.');
    }

    const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/verify-login-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        verifyOtpToken: pendingLoginOtp.token,
        otp,
      }),
    });

    const data = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(data?.message || `Login verification failed (${response.status}). Please try again.`);
    }

    // The backend returns 403 + APP_PASSWORD_REQUIRED + setAppPasswordToken
    // (same shape as the original PIN-login graduation response). Stash it
    // for SetAppPasswordPage and clear the OTP payload.
    if (data?.errorCode === 'APP_PASSWORD_REQUIRED' && data?.setAppPasswordToken) {
      setPendingAppPassword({
        token: data.setAppPasswordToken,
        phoneNumber: data?.user?.phone || pendingLoginOtp.phoneNumber,
        firstName: data?.user?.firstName ?? null,
        lastName: data?.user?.lastName ?? null,
      });
      setPendingLoginOtp(null);
    } else {
      // Unexpected success shape — fail safely back to login.
      setPendingLoginOtp(null);
      throw new Error('Login verification completed but no password setup followed. Please log in again.');
    }
  };

  const clearPendingLoginOtp = () => setPendingLoginOtp(null);

  // USSD-PIN → app-password graduation (Option B, 2026-08-14).
  // Consumes the scoped setAppPasswordToken stashed by login() when a
  // non-graduated USSD user logged in with their PIN. On success the backend
  // sets the password, flips app_password_set=true and bumps tokenVersion;
  // the scoped token is consumed. The caller (SetAppPasswordPage) then logs
  // in normally with the new password.
  const setAppPassword = async (newPassword: string, confirmNewPassword: string) => {
    if (!pendingAppPassword?.token) {
      throw new Error('No active set-app-password session. Please log in again.');
    }
    if (newPassword !== confirmNewPassword) {
      throw new Error('New password and confirmation do not match.');
    }

    const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/auth/set-app-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        setAppPasswordToken: pendingAppPassword.token,
        newPassword,
        confirmNewPassword,
      }),
    });

    const data = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(data?.message || `Setting app password failed (${response.status}). Please try again.`);
    }
    // Scoped token consumed; clear the pending graduation payload.
    setPendingAppPassword(null);
  };

  const clearPendingAppPassword = () => setPendingAppPassword(null);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isLoading,
      refreshToken,
      refreshUserStatus,
      requiresKYC,
      changePassword,
      pendingLoginOtp,
      verifyLoginOtp,
      clearPendingLoginOtp,
      pendingAppPassword,
      setAppPassword,
      clearPendingAppPassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export types for use in other components
export type { User, LoginCredentials, RegistrationData, AuthContextType, KYCStatus };